import { useNavigate, useParams } from 'react-router-dom-v6'
import { Skeleton } from '@clouway/ui'

import { AllowedScopesEditor } from '@/components/AllowedScopesEditor'
import { Permission, hasPermission, useSession } from '@/features/auth'
import { OAuthClientForm } from '../components/OAuthClientForm'
import { useOAuthClientAllowedScopes, useOAuthClients, usePutOAuthClientAllowedScopes, useUpdateOAuthClient } from '../hooks'
import { toInput } from '../transform/oauthClientTransformer'
import type { OAuthClientInput } from '../types'

export function EditOAuthClient() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: clients, isLoading } = useOAuthClients()
  const { mutateAsync: update } = useUpdateOAuthClient()
  const client = clients?.find((candidate) => candidate.id === id)
  const { access } = useSession()
  // Mirrors the gates the server enforces on this client's ceiling endpoints:
  // GET only needs ServiceAccounts.READ, PUT needs ServiceAccounts.WRITE — so
  // a reader gets the editor in read-only mode (same as the service-account
  // edit screen) instead of the block being hidden outright.
  const canReadScopes = hasPermission(access, Permission.serviceAccounts.get)
  const canManageScopes = hasPermission(access, Permission.serviceAccounts.update)
  const { data: allowedScopes, isLoading: allowedScopesLoading } = useOAuthClientAllowedScopes(canReadScopes ? id : '')
  const { mutateAsync: putAllowedScopes } = usePutOAuthClientAllowedScopes(id)

  if (isLoading || !client) return <Skeleton className="mx-auto h-80 w-full max-w-2xl" />

  const onSubmit = async (input: OAuthClientInput) => {
    await update({ id, input })
    navigate('/apis')
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
      <OAuthClientForm mode="edit" initialValues={toInput(client)} onSubmit={onSubmit} />
      {canReadScopes && (
        <AllowedScopesEditor
          allowedScopeIds={allowedScopes?.map((scope) => scope.id)}
          isLoading={allowedScopesLoading}
          onSave={putAllowedScopes}
          readOnly={!canManageScopes}
        />
      )}
    </div>
  )
}
